import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

export interface SSEEvent {
  type: string;
  data: any;
  timestamp: number;
}

interface SSEState {
  // Connection
  status: ConnectionStatus;
  connection: EventSource | null;
  error: string | null;
  tokenId: string | null; // Currently connected NFT token ID

  // Reconnection
  reconnectAttempts: number;
  maxReconnectAttempts: number;
  reconnectDelay: number;
  reconnectTimeoutId: NodeJS.Timeout | null;

  // Heartbeat
  lastHeartbeat: number | null;
  heartbeatTimeout: number;
  heartbeatIntervalId: NodeJS.Timeout | null;

  // Events
  lastEvent: SSEEvent | null;

  // Actions
  connect: (tokenId: string) => void;
  disconnect: () => void;
  setStatus: (status: ConnectionStatus) => void;
  handleEvent: (event: SSEEvent) => void;
  handleHeartbeat: () => void;
  incrementReconnectAttempts: () => void;
  resetReconnectAttempts: () => void;
  setError: (error: string | null) => void;
  scheduleReconnect: () => void;
  checkHeartbeat: () => void;
  startHeartbeatMonitor: () => void;
  stopHeartbeatMonitor: () => void;
}

export const useSSEStore = create<SSEState>()(
  immer((set, get) => ({
    // Initial state
    status: 'disconnected',
    connection: null,
    error: null,
    tokenId: null,
    reconnectAttempts: 0,
    maxReconnectAttempts: 10,
    reconnectDelay: 1000,
    reconnectTimeoutId: null,
    lastHeartbeat: null,
    heartbeatTimeout: 30000, // 30 seconds
    heartbeatIntervalId: null,
    lastEvent: null,

    // Actions
    connect: (tokenId: string) => {
      const state = get();

      // Close existing connection if any
      if (state.connection) {
        state.disconnect();
      }

      set((draft) => {
        draft.status = 'connecting';
        draft.error = null;
        draft.tokenId = tokenId;
      });

      try {
        const eventSource = new EventSource(`/api/v1/nfts/${tokenId}/updates`);

        eventSource.onopen = () => {
          console.log('[SSE] Connected');
          set((draft) => {
            draft.status = 'connected';
            draft.connection = eventSource;
            draft.reconnectAttempts = 0;
            draft.reconnectDelay = 1000;
            draft.error = null;
          });

          // Start heartbeat monitoring
          get().startHeartbeatMonitor();
        };

        eventSource.onerror = (error) => {
          console.error('[SSE] Connection error:', error);
          set((draft) => {
            draft.status = 'reconnecting';
          });

          get().disconnect();
          get().scheduleReconnect();
        };

        // Listen for heartbeat events
        eventSource.addEventListener('heartbeat', () => {
          get().handleHeartbeat();
        });

        // Store connection immediately for event listener registration
        set((draft) => {
          draft.connection = eventSource;
        });
      } catch (error) {
        console.error('[SSE] Failed to create EventSource:', error);
        set((draft) => {
          draft.status = 'disconnected';
          draft.error = 'Failed to establish connection';
        });
      }
    },

    disconnect: () => {
      const state = get();

      if (state.connection) {
        state.connection.close();
      }

      // Clear reconnect timeout
      if (state.reconnectTimeoutId) {
        clearTimeout(state.reconnectTimeoutId);
      }

      // Stop heartbeat monitor
      state.stopHeartbeatMonitor();

      set((draft) => {
        draft.connection = null;
        draft.status = 'disconnected';
        draft.reconnectTimeoutId = null;
        draft.tokenId = null;
      });
    },

    setStatus: (status) =>
      set((draft) => {
        draft.status = status;
      }),

    handleEvent: (event) =>
      set((draft) => {
        draft.lastEvent = event;
      }),

    handleHeartbeat: () => {
      set((draft) => {
        draft.lastHeartbeat = Date.now();
      });
    },

    incrementReconnectAttempts: () =>
      set((draft) => {
        draft.reconnectAttempts++;
        // Exponential backoff: 1s, 2s, 4s, 8s, max 30s
        draft.reconnectDelay = Math.min(draft.reconnectDelay * 2, 30000);
      }),

    resetReconnectAttempts: () =>
      set((draft) => {
        draft.reconnectAttempts = 0;
        draft.reconnectDelay = 1000;
      }),

    setError: (error) =>
      set((draft) => {
        draft.error = error;
      }),

    scheduleReconnect: () => {
      const state = get();

      if (state.reconnectAttempts >= state.maxReconnectAttempts) {
        console.error('[SSE] Max reconnection attempts reached');
        set((draft) => {
          draft.status = 'disconnected';
          draft.error = 'Unable to connect. Please refresh the page.';
        });
        return;
      }

      if (!state.tokenId) {
        console.error('[SSE] No tokenId available for reconnection');
        return;
      }

      state.incrementReconnectAttempts();

      const timeoutId = setTimeout(() => {
        console.log(
          `[SSE] Reconnecting... (attempt ${state.reconnectAttempts}/${state.maxReconnectAttempts})`
        );
        const currentTokenId = get().tokenId;
        if (currentTokenId) {
          get().connect(currentTokenId);
        }
      }, state.reconnectDelay);

      set((draft) => {
        draft.reconnectTimeoutId = timeoutId;
      });
    },

    checkHeartbeat: () => {
      const state = get();
      const now = Date.now();

      if (
        state.lastHeartbeat &&
        now - state.lastHeartbeat > state.heartbeatTimeout
      ) {
        console.warn('[SSE] Heartbeat timeout - connection may be stale');
        // Connection is stale, reconnect
        state.disconnect();
        state.scheduleReconnect();
      }
    },

    startHeartbeatMonitor: () => {
      const state = get();

      // Clear existing monitor
      state.stopHeartbeatMonitor();

      // Check heartbeat every 15 seconds
      const intervalId = setInterval(() => {
        get().checkHeartbeat();
      }, 15000);

      set((draft) => {
        draft.heartbeatIntervalId = intervalId;
        draft.lastHeartbeat = Date.now(); // Initialize heartbeat
      });
    },

    stopHeartbeatMonitor: () => {
      const state = get();

      if (state.heartbeatIntervalId) {
        clearInterval(state.heartbeatIntervalId);
        set((draft) => {
          draft.heartbeatIntervalId = null;
        });
      }
    },
  }))
);
